// Google Analytics event tracking utilities

export const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_ID || "";

/**
 * Make sure a gtag() function exists BEFORE anything tries to use it.
 *
 * Every tracker below used to be wrapped in an `if (window.gtag)` check, which
 * silently threw the event away whenever the tag had not finished loading — and
 * for a long time nothing loaded it at all, so no event ever fired. The tag now
 * loads via components/analytics/GoogleTag.tsx, but it is injected after
 * hydration, so a page's own effects can still run first.
 *
 * Google's snippet is designed for exactly this: gtag() is a thin shim that
 * pushes into window.dataLayer, and gtag.js replays that queue once it arrives.
 * Defining the shim here means an early event is QUEUED rather than lost.
 */
function ensureGtag(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== "function") {
    w.gtag = function gtag() {
      w.dataLayer.push(arguments);
    };
  }
  return true;
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/pages
export const pageview = (url: string) => {
  if (ensureGtag()) {
    window.gtag("config", GA_TRACKING_ID, {
      page_path: url,
    });
  }
};

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
export const event = ({
  action,
  category,
  label,
  value,
}: {
  action: string;
  category: string;
  label?: string;
  value?: number;
}) => {
  if (ensureGtag()) {
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Product view event
export const trackProductView = (product: {
  id: string;
  name: string;
  category?: string;
  price: number;
  brand?: string;
}) => {
  event({
    action: "view_item",
    category: "ecommerce",
    label: product.name,
    value: product.price,
  });

  // Enhanced ecommerce tracking
  if (ensureGtag()) {
    window.gtag("event", "view_item", {
      currency: "UGX",
      value: product.price,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          item_category: product.category || "Uncategorized",
          item_brand: product.brand || "Unknown",
          price: product.price,
          quantity: 1,
        },
      ],
    });
  }
};

// Add to cart event
export const trackAddToCart = (product: {
  id: string;
  name: string;
  category?: string;
  price: number;
  brand?: string;
  quantity: number;
}) => {
  event({
    action: "add_to_cart",
    category: "ecommerce",
    label: product.name,
    value: product.price * product.quantity,
  });

  // Enhanced ecommerce tracking
  if (ensureGtag()) {
    window.gtag("event", "add_to_cart", {
      currency: "UGX",
      value: product.price * product.quantity,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          item_category: product.category || "Uncategorized",
          item_brand: product.brand || "Unknown",
          price: product.price,
          quantity: product.quantity,
        },
      ],
    });
  }
};

// Remove from cart event
export const trackRemoveFromCart = (product: {
  id: string;
  name: string;
  category?: string;
  price: number;
  brand?: string;
  quantity: number;
}) => {
  event({
    action: "remove_from_cart",
    category: "ecommerce",
    label: product.name,
    value: product.price * product.quantity,
  });

  // Enhanced ecommerce tracking
  if (ensureGtag()) {
    window.gtag("event", "remove_from_cart", {
      currency: "UGX",
      value: product.price * product.quantity,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          item_category: product.category || "Uncategorized",
          item_brand: product.brand || "Unknown",
          price: product.price,
          quantity: product.quantity,
        },
      ],
    });
  }
};

// Search event
export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  event({
    action: "search",
    category: "engagement",
    label: searchTerm,
    value: resultsCount,
  });

  // Enhanced search tracking
  if (ensureGtag()) {
    window.gtag("event", "search", {
      search_term: searchTerm,
      ...(resultsCount !== undefined && { results_count: resultsCount }),
    });
  }
};

// Category navigation event
export const trackCategoryView = (
  categoryName: string,
  categoryId?: string
) => {
  event({
    action: "view_category",
    category: "navigation",
    label: categoryName,
  });

  // Enhanced category tracking
  if (ensureGtag()) {
    window.gtag("event", "view_item_list", {
      item_list_id:
        categoryId || categoryName.toLowerCase().replace(/\s+/g, "_"),
      item_list_name: categoryName,
    });
  }
};

// Begin checkout event
export const trackBeginCheckout = (
  items: Array<{
    id: string;
    name: string;
    category?: string;
    price: number;
    brand?: string;
    quantity: number;
  }>,
  totalValue: number
) => {
  event({
    action: "begin_checkout",
    category: "ecommerce",
    label: "Checkout Started",
    value: totalValue,
  });

  // Enhanced ecommerce tracking
  if (ensureGtag()) {
    window.gtag("event", "begin_checkout", {
      currency: "UGX",
      value: totalValue,
      items: items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        item_category: item.category || "Uncategorized",
        item_brand: item.brand || "Unknown",
        price: item.price,
        quantity: item.quantity,
      })),
    });
  }
};

// Purchase event
export const trackPurchase = (
  transactionId: string,
  items: Array<{
    id: string;
    name: string;
    category?: string;
    price: number;
    brand?: string;
    quantity: number;
  }>,
  totalValue: number,
  tax?: number,
  shipping?: number
) => {
  event({
    action: "purchase",
    category: "ecommerce",
    label: transactionId,
    value: totalValue,
  });

  // Enhanced ecommerce tracking
  if (ensureGtag()) {
    window.gtag("event", "purchase", {
      transaction_id: transactionId,
      currency: "UGX",
      value: totalValue,
      tax: tax || 0,
      shipping: shipping || 0,
      items: items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        item_category: item.category || "Uncategorized",
        item_brand: item.brand || "Unknown",
        price: item.price,
        quantity: item.quantity,
      })),
    });
  }
};

// View cart event
export const trackViewCart = (
  items: Array<{
    id: string;
    name: string;
    category?: string;
    price: number;
    brand?: string;
    quantity: number;
  }>,
  totalValue: number
) => {
  event({
    action: "view_cart",
    category: "ecommerce",
    label: "Cart Viewed",
    value: totalValue,
  });

  // Enhanced ecommerce tracking
  if (ensureGtag()) {
    window.gtag("event", "view_cart", {
      currency: "UGX",
      value: totalValue,
      items: items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        item_category: item.category || "Uncategorized",
        item_brand: item.brand || "Unknown",
        price: item.price,
        quantity: item.quantity,
      })),
    });
  }
};

// Product click event
export const trackProductClick = (product: {
  id: string;
  name: string;
  category?: string;
  price: number;
  brand?: string;
  position?: number;
}) => {
  event({
    action: "select_item",
    category: "ecommerce",
    label: product.name,
  });

  // Enhanced ecommerce tracking
  if (ensureGtag()) {
    window.gtag("event", "select_item", {
      item_list_name: product.category || "Products",
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          item_category: product.category || "Uncategorized",
          item_brand: product.brand || "Unknown",
          price: product.price,
          index: product.position || 0,
        },
      ],
    });
  }
};

// Declare gtag types for TypeScript
declare global {
  interface Window {
    gtag: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
  }
}
